import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  RegistrationGroup,
  InvoiceLineItem,
} from "../../api/getEventTransactionDetails/types";
import { format } from "date-fns";
import { useState } from "react";
import { RefundPanel } from "./RefundPanel";
import { VoucherReturnPanel } from "./VoucherReturnPanel";

interface TransactionDetailsProps {
  group: RegistrationGroup;
  onRefundSuccess: () => void;
}

export function TransactionDetails({
  group,
  onRefundSuccess,
}: TransactionDetailsProps) {
  const [showRefundPanel, setShowRefundPanel] = useState(false);
  const [showVoucherReturnPanel, setShowVoucherReturnPanel] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
    } catch {
      return dateString;
    }
  };

  const hasRefundableLineItems = group.invoices.some((invoice) =>
    invoice.lineItems.some((item) => item.maxRefundableQuantity > 0),
  );

  // Calculate available vouchers to return (not already returned)
  const availableVouchersToReturn = group.voucherUsageHistory.filter(
    (v) => !v.isReturned,
  ).length;

  const handleRefundSuccess = () => {
    setShowRefundPanel(false);
    onRefundSuccess();
  };

  const handleVoucherReturnSuccess = () => {
    setShowVoucherReturnPanel(false);
    onRefundSuccess();
  };

  return (
    <div className="bg-muted/30 p-6 space-y-6 border-t">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Summary Section */}
          <div>
            <h3 className="text-sm font-medium mb-3">Summary</h3>
            <div className="rounded-lg border bg-card p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Paid</span>
                <span className="font-medium">
                  {formatCurrency(group.totalPaid)}
                </span>
              </div>
              {group.refundedAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Refunded</span>
                  <span className="font-medium text-destructive">
                    -{formatCurrency(group.refundedAmount)}
                  </span>
                </div>
              )}
              {group.pendingRefundAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Pending Refund</span>
                  <span className="font-medium text-orange-600 dark:text-orange-400">
                    -{formatCurrency(group.pendingRefundAmount)}
                  </span>
                </div>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between text-sm">
                <span className="font-medium">Remaining Refundable</span>
                <span className="font-medium">
                  {formatCurrency(group.remainingRefundableAmount)}
                </span>
              </div>
            </div>
          </div>

          {/* Attendees Section */}
          <div>
            <h3 className="text-sm font-medium mb-3">Attendees</h3>
            <div className="text-sm text-muted-foreground">
              {group.actualAttendeeCount} total attendees
              {group.voucherCount > 0 &&
                ` (${group.voucherCount} using vouchers)`}
            </div>
          </div>

          {/* Vouchers Section */}
          {group.voucherCount > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium">Vouchers</h3>
                {availableVouchersToReturn > 0 && !showVoucherReturnPanel && (
                  <Button
                    onClick={() => setShowVoucherReturnPanel(true)}
                    size="sm"
                    variant="outline"
                  >
                    Return Vouchers
                  </Button>
                )}
              </div>
              <div className="rounded-lg border bg-card p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Total Vouchers Used
                  </span>
                  <span className="font-medium">{group.voucherCount}</span>
                </div>
                {availableVouchersToReturn < group.voucherCount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Already Returned
                    </span>
                    <span className="font-medium text-orange-600 dark:text-orange-400">
                      {group.voucherCount - availableVouchersToReturn}
                    </span>
                  </div>
                )}
                <Separator className="my-2" />
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Available to Return</span>
                  <span className="font-medium">
                    {availableVouchersToReturn}
                  </span>
                </div>
              </div>

              {/* Voucher Return Panel */}
              {showVoucherReturnPanel && (
                <div className="mt-4">
                  <VoucherReturnPanel
                    group={group}
                    onCancel={() => setShowVoucherReturnPanel(false)}
                    onSuccess={handleVoucherReturnSuccess}
                  />
                </div>
              )}
            </div>
          )}

          {/* Voucher Usage History */}
          {group.voucherUsageHistory.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-3">
                Voucher Usage History
              </h3>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Voucher</TableHead>
                      <TableHead>Attendee</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.voucherUsageHistory.map((voucher) => (
                      <TableRow
                        className={voucher.isReturned ? "bg-muted/30" : ""}
                        key={voucher.id}
                      >
                        <TableCell className="font-medium">
                          <div>{voucher.voucherName}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {voucher.attendeeType === "enrolled"
                              ? "Enrolled"
                              : "Waitlisted"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {voucher.usageNotes || "-"}
                          </div>
                        </TableCell>
                        <TableCell>
                          {voucher.isReturned ? (
                            <div>
                              <div className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                                Returned: {formatDate(voucher.returnedAt)}
                              </div>
                              {voucher.returnReason && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Reason:{" "}
                                  {voucher.returnReason
                                    .replace(/_/g, " ")
                                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                                </div>
                              )}
                              {voucher.returnedBy && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  By: {voucher.returnedBy}
                                </div>
                              )}
                              {voucher.returnNotes && (
                                <div className="text-xs text-muted-foreground mt-1 italic">
                                  {voucher.returnNotes}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm">
                              Redeemed: {formatDate(voucher.redeemedAt)}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Contact Information */}
          {(group.contactName || group.contactEmail || group.contactPhone) && (
            <div>
              <h3 className="text-sm font-medium mb-3">Contact Information</h3>
              <div className="text-sm space-y-1">
                {group.contactName && (
                  <div className="text-muted-foreground">
                    {group.contactName}
                  </div>
                )}
                {group.contactEmail && (
                  <div className="text-muted-foreground">
                    {group.contactEmail}
                  </div>
                )}
                {group.contactPhone && (
                  <div className="text-muted-foreground">
                    {group.contactPhone}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Invoice Line Items Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium">Line Items</h3>
              {hasRefundableLineItems && !showRefundPanel && (
                <Button
                  onClick={() => setShowRefundPanel(true)}
                  size="sm"
                  variant="outline"
                >
                  Refund Line Items
                </Button>
              )}
            </div>

            {group.invoices.map((invoice) => (
              <div className="space-y-3" key={invoice.id}>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>Invoice {invoice.invoiceNumber}</span>
                  <Badge className="text-xs" variant="outline">
                    {invoice.status}
                  </Badge>
                </div>

                <div className="rounded-lg border bg-card divide-y">
                  {invoice.lineItems.map((lineItem) => (
                    <LineItemRow key={lineItem.id} lineItem={lineItem} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Refund Panel */}
          {showRefundPanel && (
            <RefundPanel
              group={group}
              onCancel={() => setShowRefundPanel(false)}
              onSuccess={handleRefundSuccess}
            />
          )}

          {/* Payments Section */}
          {group.payments.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-3">Payments</h3>
              <div className="space-y-2">
                {group.payments.map((payment) => (
                  <div
                    className="rounded-lg border bg-card p-3 text-sm"
                    key={payment.id}
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="font-medium">
                          {formatCurrency(payment.amount)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {payment.cardType} •••• {payment.cardLast4}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(payment.createdAt)}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {payment.transactionId}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Refund History */}
          {group.refundHistory.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-3">Refund History</h3>
              <div className="space-y-2">
                {group.refundHistory.map((refund) => (
                  <div
                    className="rounded-lg border bg-card p-3 text-sm"
                    key={refund.id}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-medium">
                        {formatCurrency(refund.requestedAmount)}
                      </div>
                      <Badge
                        variant={
                          refund.status === "completed"
                            ? "default"
                            : refund.status === "failed"
                              ? "destructive"
                              : "secondary"
                        }
                      >
                        {refund.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <div>{refund.reason}</div>
                      <div>
                        Requested by {refund.requestedBy} on{" "}
                        {formatDate(refund.createdAt)}
                      </div>
                      {refund.processedAt && (
                        <div>Processed on {formatDate(refund.processedAt)}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LineItemRow({ lineItem }: { lineItem: InvoiceLineItem }) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const isNegative = lineItem.unitPrice < 0;
  const isFullyRefunded = lineItem.isFullyRefunded && !isNegative; // Don't show refunded badge for discounts
  const hasPartialRefund =
    lineItem.totalRefundedQuantity > 0 && !isFullyRefunded && !isNegative;

  return (
    <div className={`p-3 ${isFullyRefunded ? "bg-muted/50" : ""}`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span
              className={`text-sm ${isFullyRefunded ? "text-muted-foreground line-through" : ""}`}
            >
              {lineItem.description}
            </span>
            {isFullyRefunded && (
              <Badge className="text-xs" variant="secondary">
                Refunded
              </Badge>
            )}
            {hasPartialRefund && (
              <Badge className="text-xs" variant="outline">
                Partial Refund
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Qty: {lineItem.quantity} × {formatCurrency(lineItem.unitPrice)}
            {hasPartialRefund && (
              <span className="ml-2">
                (Refunded: {lineItem.totalRefundedQuantity.toFixed(2)} -{" "}
                {formatCurrency(lineItem.totalRefundedAmount)})
              </span>
            )}
          </div>
        </div>
        <div
          className={`text-sm font-medium ${isNegative ? "text-green-600 dark:text-green-400" : ""}`}
        >
          {formatCurrency(lineItem.lineTotal)}
        </div>
      </div>
    </div>
  );
}
